<script setup>
import { computed, ref, onMounted } from "vue";
import { useScrollStore } from "@/stores/storeScroll";
import { useSettingsStore } from "@/stores/storeSettings";
import { useModals } from "@/composables/useModals";
import { scrollToSection } from "@/utils/functions";
import { sendMetrik, trackPixel, smartPixel, trackAdriver } from "@/utils/metrika";
import BaseButtonUploadReceipt from "@/components/ui/BaseButtonUploadReceipt.vue";

const storeScroll = useScrollStore();
const storeSettings = useSettingsStore();
const { openModal, modalInfo } = useModals();

const steps = [
	{
		num: 1,
		title: "Покупайте",
		text: "чай Greenfield в любом магазине",
	},
	{
		num: 2,
		title: "Регистрируйте",
		text: "чек на сайте",
		hasButton: storeSettings.isTelegramPage ? false : true,
	},
	{
		num: 3,
		title: "Копите",
		text: "листочки и обменивайте их на подарки",
		hasLink: true,
		linkText: "Что такое листочки?",
	},
	{
		num: 4,
		title: "Участвуйте",
		text: "в розыгрыше",
		hasHighlight: true,
		highlightText: "призов",
	},
];

const formatDate = (dateStr, withYear = false) => {
	if (!dateStr) return "";
	const dateOnly = dateStr.split("T")[0];
	const [year, month, day] = dateOnly.split("-").map(Number);
	const date = new Date(year, month - 1, day);
	if (isNaN(date.getTime())) return "";
	const options = withYear ? { day: "2-digit", month: "long", year: "numeric" } : { day: "2-digit", month: "long" };
	return new Intl.DateTimeFormat("ru-RU", options).format(date);
};

const formattedStartDate = computed(() => formatDate(storeSettings.startDate, false));
const formattedEndDate = computed(() => formatDate(storeSettings.endDate, true));

onMounted(() => {});
</script>
<template>
	<section class="main-screen" :style="{ paddingTop: storeScroll.paddingTop }">
		<div class="container">
			<div class="main-screen__content" id="participation">
				<div class="main-screen__header">
					<p class="main-screen__date">{{ formattedStartDate }} - {{ formattedEndDate }}</p>
					<h1 class="main-screen__title title">Greenfield Клуб исполняет мечты</h1>
				</div>
				<div class="main-screen__body">
					<ul class="main-screen__list">
						<li class="main-screen__item" v-for="step in steps" :key="step.num">
							<div class="main-screen__item-number">{{ step.num }}</div>
							<div class="main-screen__item-content">
								<h3 class="main-screen__item-title title3">{{ step.title }}</h3>
								<p class="main-screen__item-text">
									{{ step.text }}
									<a v-if="step.hasHighlight" href="#prizes" @click.prevent="scrollToSection('prizes')">{{ step.highlightText }}</a>
								</p>
								<BaseButtonUploadReceipt type-btn="upload_check" v-if="step.hasButton" class="main-screen__item-btn --small">Загрузить чек</BaseButtonUploadReceipt>
								<button v-if="step.hasLink" class="main-screen__item-link" @click.prevent="openModal('modalInfo', { content: modalInfo[0], size: 'w400' })">{{ step.linkText }}</button>
							</div>
						</li>
					</ul>
					<div class="main-screen__btns">
						<BaseButtonUploadReceipt type-btn="upload_check_mechanics" v-if="!storeSettings.isTelegramPage" class="main-screen__btn">Участвовать</BaseButtonUploadReceipt>
						<a @click="sendMetrik('reachGoal', 'tg'), trackPixel('VIEW', 'telegram_bot'), smartPixel('click_go_to_tg')"  v-if="!storeSettings.isTelegramPage" :href="storeSettings.telegramUrl" target="_blank" rel="noopener noreferrer" class="main-screen__link-tg btn">
							<svg width="40" height="40"><use href="#telegram"></use></svg>
						</a>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
.main-screen {
	background-image: image-set(url("@/assets/img/main/main_img.webp") type("image/webp") 1x);
	background-repeat: no-repeat;
	background-position: center; /* x y*/
	background-size: cover; /* w h*/

	@media (max-width: vars.$t) {
		background-position: 70% center;
	}
	@media (max-width: vars.$mn) {
		background-position: 60% center;
	}
	@media (max-width: vars.$x480) {
		background-image: url("@/assets/img/main/main_img_mobile.webp");
		background-position: center -12%;
		background-size: contain;
	}
	@media (max-width: vars.$x425) {
		background-position: center -12%;
	}
	@media (max-width: vars.$x393) {
		background-position: center -12%;
	}
	@media (max-width: vars.$x360) {
		background-position: center -4%;
	}
}
.main-screen__content {
	max-width: 82rem;
	display: grid;
	align-items: start;
	padding: 2.5rem 0 6.5rem;
	@media (max-width: vars.$x480) {
		padding: 1.7rem 0 2rem;
		gap: 31rem;
	}
	@media (max-width: vars.$x425) {
		gap: 34rem;
	}
	@media (max-width: vars.$x393) {
		gap: 32rem;
	}
}
.main-screen__header {
	display: grid;
	gap: 1.7rem;
	margin-bottom: 1rem;
	@media (max-width: vars.$x480) {
		gap: 0.5rem;
	}
}
.main-screen__date {
	font-weight: 500;
	font-size: 3.2rem;
	line-height: 110%;
	color: var(--text-accent-2-100);
	@media (max-width: vars.$xx) {
		font-size: 1.8rem;
	}
}
.main-screen__body {
	position: relative;
	&::before {
		display: none;
		position: absolute;
		content: "";
		background-image: url("@/assets/img/main/main_bg_mobile.webp");
		background-repeat: no-repeat;
		background-position: 0px 0px; /* x y*/
		background-size: cover; /* w h*/
		inset: calc((var(--padding-container) * -1) + -5px) 0 0 calc((var(--padding-container) * -1.1));
		@media (max-width: vars.$x480) {
			display: block;
		}
		@media (max-width: vars.$x393) {
			inset: calc((var(--padding-container) * -1) + 0px) 0 0 calc((var(--padding-container) * -1.1));
		}
		@media (max-width: vars.$x360) {
			inset: calc((var(--padding-container) * -1) + -20px) 0 0 calc((var(--padding-container) * -1.1));
		}
	}
}
.main-screen__list {
	display: grid;
	align-items: start;
	gap: 1rem;
	margin-bottom: 3rem;
	@media (max-width: vars.$x480) {
		gap: 0.6rem;
		margin-bottom: 2rem;
	}
}
.main-screen__item {
	display: flex;
	align-items: start;
	gap: 2rem;
	@media (max-width: vars.$x480) {
		gap: 1rem;
	}
}
.main-screen__item-number {
	font-family: var(--second-family);
	font-weight: 700;
	font-size: 6rem;
	line-height: 110%;
	text-transform: uppercase;
	text-align: center;
	color: var(--text-accent-2-100);
	width: 4.2rem;
	@media (max-width: vars.$xx) {
		font-size: 3rem;
	}
}
.main-screen__item-content {
	display: grid;
	align-items: start;
	gap: 0px;
	@media (max-width: vars.$x480) {
		width: 100%;
	}
}
.main-screen__item-title {
	color: var(--text-accent-2-100);
}
.main-screen__item-text {
	a {
		position: relative;
		text-decoration: underline;
		text-decoration-skip-ink: none;
		color: var(--text-accent-2-100);
		@media (hover: hover) {
			&:hover {
				text-decoration: none;
			}
		}
	}
}
.main-screen__item-btn {
	margin-top: 1rem;
	width: fit-content;
	@media (max-width: vars.$x480) {
		width: 80%;
	}
}
.main-screen__item-link {
	position: relative;
	width: fit-content;
	font-weight: 500;
	font-size: 1.6rem;
	line-height: 160%;
	text-decoration: underline;
	text-decoration-skip-ink: none;
	color: var(--text-accent-2-100);
	@media (hover: hover) {
		&:hover {
			text-decoration: none;
		}
	}
}

.main-screen__btns {
	display: flex;
	align-items: center;
	gap: 1.6rem;
}

.main-screen__link-tg {
	padding: 0;
	border-radius: 2rem;
	width: 8rem;
	height: 8rem;
	flex-shrink: 0;
	@media (max-width: vars.$x480) {
		width: 4.8rem;
		height: 4.8rem;
		border-radius: 1.6rem;
	}
	svg {
		width: 4rem;
		height: 4rem;
		padding-right: 0.5rem;
		@media (max-width: vars.$x480) {
			width: 3.2rem;
			height: 3.2rem;
		}
	}
}

.main-screen__btn {
	width: fit-content;
	@media (max-width: vars.$x480) {
		width: 100%;
	}
}

.v-enter-active,
.v-leave-active {
	transition:
		opacity 0.8s ease,
		transform 0.8s ease;
}

.v-enter-from,
.v-leave-to {
	opacity: 0;
	transform: translateY(100%);
}
</style>
